import { z } from 'zod';
import { useEffect } from 'react';
import {
    CustomErrorType,
    CustomErrorParams,
    createZodFormResolver,
    passwordRequirements,
    useFormControls,
} from '@mews/framework';
import { getPasswordStrength, PasswordStrength } from '@optimus-web/core';
import { GeneralArea, useRepositoryMutation } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { ChangePasswordFormValues, ChangePasswordFields } from './types';
import { CHANGE_PASSWORD_FORM_ID } from './constants';

const resolver = createZodFormResolver<ChangePasswordFormValues>(
    z
        .object({
            [ChangePasswordFields.NewPassword]: z
                .string()
                .refine((password) => password.length > 0, {
                    params: {
                        errorType: CustomErrorType.Other,
                        textKey: TextKey.PasswordEmptyErrorMessage,
                    } satisfies CustomErrorParams,
                })
                .refine((password) => getPasswordStrength(passwordRequirements, password) === PasswordStrength.Good, {
                    params: {
                        errorType: CustomErrorType.Other,
                        textKey: TextKey.PasswordWeakErrorMessage,
                    } satisfies CustomErrorParams,
                }),
            [ChangePasswordFields.ConfirmNewPassword]: z.string(),
        })
        .refine(({ newPassword, confirmNewPassword }) => newPassword === confirmNewPassword, {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.PasswordsMustBeTheSame,
            } satisfies CustomErrorParams,
            path: [ChangePasswordFields.ConfirmNewPassword],
        })
);

const defaultValues: Record<ChangePasswordFields, string> = {
    [ChangePasswordFields.NewPassword]: '',
    [ChangePasswordFields.ConfirmNewPassword]: '',
};

export const useChangePasswordForm = (oldPassword: string) => {
    const { mutate, isPending, isSuccess, error } = useRepositoryMutation('generalUsers', 'updatePassword', {
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    const handleSubmit = (values: ChangePasswordFormValues) => {
        mutate({
            oldPassword,
            newPassword: values.newPassword,
            application: GeneralArea.Commander,
        });
    };

    const { renderFormContent, setError, submit, reset } = useFormControls<ChangePasswordFormValues>({
        id: CHANGE_PASSWORD_FORM_ID,
        onSubmit: handleSubmit,
        resolver,
        defaultValues,
    });

    useEffect(() => {
        if (error instanceof Error) {
            setError('newPassword', { message: error.message });
            reset(defaultValues, { keepErrors: true });
        }
    }, [error, setError, reset]);

    return { isLoading: isPending, isSuccess, submit, renderFormContent, setError };
};
